import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectHasPermission } from "../../store/slices/authSlice";

const Sidebar = () => {
  const location = useLocation();
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isOPDOpen, setIsOPDOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isMasterPagesOpen, setIsMasterPagesOpen] = useState(false);
  const [isLogsOpen, setIsLogsOpen] = useState(false);

  const canManageUsers = useSelector((state) =>
    selectHasPermission(state, "users-manage"),
  );
  const canManageRoles = useSelector((state) =>
    selectHasPermission(state, "roles-manage"),
  );
  const canManageSessionTypes = useSelector((state) =>
    selectHasPermission(state, "session-types-manage"),
  );
  const canManageSessionCategories = useSelector((state) =>
    selectHasPermission(state, "session-categories-manage"),
  );
  const canManageSubSessionTypes = useSelector((state) =>
    selectHasPermission(state, "sub-session-types-manage"),
  );
  const canManagePatients = useSelector((state) =>
    selectHasPermission(state, "patients-manage"),
  );
  const canManageCases = useSelector((state) =>
    selectHasPermission(state, "case-manage"),
  );
  const canManageUserSessions = useSelector((state) =>
    selectHasPermission(state, "user-sessions-manage"),
  );
  const canManageCaseManagement = useSelector((state) =>
    selectHasPermission(state, "case-management-manage"),
  );
  const canManageInquiries = useSelector((state) =>
    selectHasPermission(state, "inquiries-manage"),
  );
  const canManageConsent = useSelector((state) =>
    selectHasPermission(state, "consents-manage"),
  );
  const canManageInjection = useSelector((state) =>
    selectHasPermission(state, "injections-manage"),
  );
  const canManageBelonging = useSelector((state) =>
    selectHasPermission(state, "belongings-manage"),
  );
  const canManageFamilyPlan = useSelector((state) =>
    selectHasPermission(state, "family-plan-manage"),
  );
  const canManageSanaNotes = useSelector((state) =>
    selectHasPermission(state, "sana-note-manage"),
  );
  const canManageNursingRecord = useSelector((state) =>
    selectHasPermission(state, "nursing-record-manage"),
  );
  const canManageProgressNote = useSelector((state) =>
    selectHasPermission(state, "progress-note-manage"),
  );
  const canManageFamilyMeeting = useSelector((state) =>
    selectHasPermission(state, "family-meeting-manage"),
  );
  const canManageDepotInjection = useSelector((state) =>
    selectHasPermission(state, "depot-injections-manage"),
  );
  const canManageVitalsMonitor = useSelector((state) =>
    selectHasPermission(state, "vital-monitor-manage"),
  );
  const canManageNursingOPDAssessments = useSelector((state) =>
    selectHasPermission(state, "nursing-opd-assessment-manage"),
  );
  const canManageDoctorOPDAssessments = useSelector((state) =>
    selectHasPermission(state, "doctor-opd-assessment-manage"),
  );
  const canManageReportGeneral = useSelector((state) =>
    selectHasPermission(state, "report-general-manage"),
  );
  const canManageReportTracking = useSelector((state) =>
    selectHasPermission(state, "report-tracking-metrics-manage"),
  );
  const canManageHomeVisitReport = useSelector((state) =>
    selectHasPermission(state, "report-home-visit-manage"),
  );
  const canManageOPDReport = useSelector((state) =>
    selectHasPermission(state, "report-opd-manage"),
  );
  const canManageReportSessions = useSelector((state) =>
    selectHasPermission(state, "report-sessions-manage"),
  );
  const canManageReportCaseManagement = useSelector((state) =>
    selectHasPermission(state, "report-case-management-manage"),
  );
  const canManageReportFeedback = useSelector((state) =>
    selectHasPermission(state, "report-feedback-manage"),
  );
  const canManageReportICF = useSelector((state) =>
    selectHasPermission(state, "report-individual-client-file-manage"),
  );
  const canManageReportExercise = useSelector((state) =>
    selectHasPermission(state, "report-exercise-monthly-manage"),
  );
  const canManageReportADL = useSelector((state) =>
    selectHasPermission(state, "report-adl-record-manage"),
  );
  const canManageFormLogs = useSelector((state) =>
    selectHasPermission(state, "form-logs-manage"),
  );
  const canManageAuditLogs = useSelector((state) =>
    selectHasPermission(state, "audit-logs-manage"),
  );

  const hasMasterPagesPermissions = canManageSessionCategories || canManageSessionTypes || canManageSubSessionTypes;
  const hasAdminPermissions = canManageUsers || canManageRoles;
  const hasOPDPermissions = canManageNursingOPDAssessments || canManageDoctorOPDAssessments;
  const hasReportPermissions = canManageReportGeneral || canManageReportTracking || canManageHomeVisitReport || canManageOPDReport || canManageReportSessions || canManageReportCaseManagement || canManageReportFeedback || canManageReportICF || canManageReportExercise || canManageReportADL;
  const hasLogPermissions = canManageFormLogs || canManageAuditLogs;

  // Check if any admin sub-item is active
  const isAdminActive =
    location.pathname === "/users" ||
    location.pathname === "/roles";

  const isMasterPagesActive =
    location.pathname.startsWith("/session-categories") ||
    location.pathname.startsWith("/session-types") ||
    location.pathname.startsWith("/sub-session-types");

  const isLogsActive = location.pathname.startsWith("/logs");

  const [isPhase04Open, setIsPhase04Open] = useState(false);

  const isOPDActive = location.pathname.startsWith("/nursing-opd-assessments") ||
    location.pathname.startsWith("/doctor-opd-assessments");

  useEffect(() => {
    if (isAdminActive) setIsAdminOpen(true);
    if (isOPDActive) setIsOPDOpen(true);
    if (location.pathname.startsWith("/reports")) setIsReportsOpen(true);
    if (isMasterPagesActive) setIsMasterPagesOpen(true);
    if (isLogsActive) setIsLogsOpen(true);
  }, [location.pathname, isAdminActive, isOPDActive, isMasterPagesActive, isLogsActive]);

  return (
    <aside
      id="layout-menu"
      className="layout-menu menu-vertical menu bg-menu-theme"
      style={{
        position: "sticky",
        top: 0,
        height: "100vh",
        overflowY: "auto",
      }}
    >
      <div className="app-brand demo" style={{ height: "100px" }}>
        <Link to="/dashboard" className="app-brand-link">
          <span className="app-brand-logo demo">
            <img
              src={require("../../assets/images/logo-samplee.png")}
              alt="Recovery House Logo"
            // style={{ height: '50px', width: '100px' }}
            />
          </span>
          {/* <span className="app-brand-text demo menu-text fw-semibold ms-2">
            Recovery House
          </span> */}
        </Link>
        <a href="#" className="layout-menu-toggle menu-link text-large ms-auto">
          {/* Keep your menu toggle icon SVG */}
        </a>
      </div>
      <div className="menu-inner-shadow"></div>
      <ul className="menu-inner py-1">
        {/* Dashboards */}
        <li
          className={`menu-item ${location.pathname === "/dashboard" ? "active" : ""
            }`}
        >
          <Link to="/dashboard" className="menu-link">
            <i className="menu-icon tf-icons ri-home-smile-line"></i>
            <div data-i18n="Dashboards">Dashboard</div>
          </Link>
        </li>
        {/* Inquiry */}
        {canManageInquiries && (
          <li
            className={`menu-item ${location.pathname === "/inquiries" ? "active" : ""
              }`}
          >
            <Link to="/inquiries" className="menu-link">
              <i className="menu-icon tf-icons ri-file-list-3-line"></i>
              <div data-i18n="Inquiry List">Inquiry Form</div>
            </Link>
          </li>
        )}

        {/* Patients */}
        {canManagePatients && (
          <li
            className={`menu-item ${location.pathname === "/patients" ? "active" : ""
              }`}
          >
            <Link to="/patients" className="menu-link">
              <i className="menu-icon tf-icons ri-user-line"></i>
              <div data-i18n="Active Clients">Active Clients</div>
            </Link>
          </li>
        )}
        {canManageCases && (
          <li
            className={`menu-item ${location.pathname.startsWith("/cases") ? "active" : ""}`}
          >
            <Link to="/cases" className="menu-link">
              <i className="menu-icon tf-icons ri-folder-user-line"></i>
              <div data-i18n="Registrations">Registrations</div>
            </Link>
          </li>
        )}
        {canManageUserSessions && (
          <li
            className={`menu-item ${location.pathname.startsWith("/sessions") ? "active" : ""}`}
          >
            <Link to="/sessions" className="menu-link">
              <i className="menu-icon tf-icons ri-discuss-line"></i>
              <div data-i18n="Sessions">Sessions</div>
            </Link>
          </li>
        )}
        {canManageCaseManagement && (
          <li
            className={`menu-item ${location.pathname.startsWith("/case-management") ? "active" : ""}`}
          >
            <Link to="/case-management" className="menu-link">
              <i className="menu-icon tf-icons ri-briefcase-line"></i>
              <div data-i18n="Case Management">Case Management</div>
            </Link>
          </li>
        )}

        {hasOPDPermissions && (
          <li
            className={`menu-item ${isOPDOpen ? "open" : ""} ${isOPDActive ? "active" : ""}`}
          >
            <a
              href="#"
              className="menu-link menu-toggle"
              onClick={(e) => {
                e.preventDefault();
                setIsOPDOpen(!isOPDOpen); // Use the new toggle state here
              }}
            >
              <i className="menu-icon tf-icons ri-user-add-line"></i>
              <div data-i18n="OPD Registration">OPD Registrations</div>
            </a>
            <ul className="menu-sub">
              {canManageNursingOPDAssessments && (
                <li
                  className={`menu-item ${location.pathname.startsWith("/nursing-opd-assessments") ? "active" : ""}`}
                >
                  <Link to="/nursing-opd-assessments" className="menu-link">
                    <i className="menu-icon tf-icons ri-stethoscope-line"></i>
                    <div data-i18n="Nursing OPD Assessment">Nursing Assessment</div>
                  </Link>
                </li>)}
              {canManageDoctorOPDAssessments && (
                <li
                  className={`menu-item ${location.pathname.startsWith("/doctor-opd-assessments") ? "active" : ""}`}
                >
                  <Link to="/doctor-opd-assessments" className="menu-link">
                    <i className="menu-icon tf-icons ri-user-star-line"></i>
                    <div data-i18n="Doctor OPD Assessment">Doctor Assessment</div>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}
        {/* Reports */}
        {hasReportPermissions && (
          <li
            className={`menu-item ${isReportsOpen ? "open" : ""} ${location.pathname.startsWith("/reports") ? "active" : ""}`}
          >
            <a
              href="#"
              className="menu-link menu-toggle"
              onClick={(e) => {
                e.preventDefault();
                setIsReportsOpen(!isReportsOpen);
              }}
            >
              <i className="menu-icon tf-icons ri-bar-chart-2-line"></i>
              <div data-i18n="Reports">Reports</div>
            </a>
            <ul className="menu-sub">
              {canManageReportGeneral && (
                <li className={`menu-item ${location.pathname === "/reports/general" ? "active" : ""}`}>
                  <Link to="/reports/general" className="menu-link">
                    <i className="menu-icon tf-icons ri-file-text-line"></i>
                    <div data-i18n="General Reports">General Reports</div>
                  </Link>
                </li>
              )}
              {canManageReportTracking && (
                <li className={`menu-item ${location.pathname === "/reports/tracking-metrics" ? "active" : ""}`}>
                  <Link to="/reports/tracking-metrics" className="menu-link">
                    <i className="menu-icon tf-icons ri-line-chart-line"></i>
                    <div data-i18n="Tracking Metrics">Tracking Metrics</div>
                  </Link>
                </li>
              )}
              {canManageHomeVisitReport && (
                <li className={`menu-item ${location.pathname === "/reports/home-visits" ? "active" : ""}`}>
                  <Link to="/reports/home-visits" className="menu-link">
                    <i className="menu-icon tf-icons ri-home-heart-line"></i>
                    <div data-i18n="Home Visits">Home Visits</div>
                  </Link>
                </li>
              )}
              {canManageOPDReport && (
                <li className={`menu-item ${location.pathname === "/reports/opds" ? "active" : ""}`}>
                  <Link to="/reports/opds" className="menu-link">
                    <i className="menu-icon tf-icons ri-hospital-line"></i>
                    <div data-i18n="OPD Reports">OPD Reports</div>
                  </Link>
                </li>
              )}
              {canManageReportSessions && (
                <li className={`menu-item ${location.pathname === "/reports/sessions" ? "active" : ""}`}>
                  <Link to="/reports/sessions" className="menu-link">
                    <i className="menu-icon tf-icons ri-calendar-check-line"></i>
                    <div data-i18n="Session Reports">Session Reports</div>
                  </Link>
                </li>
              )}
              {canManageReportCaseManagement && (
                <li className={`menu-item ${location.pathname === "/reports/case-management" ? "active" : ""}`}>
                  <Link to="/reports/case-management" className="menu-link">
                    <i className="menu-icon tf-icons ri-briefcase-line"></i>
                    <div data-i18n="Case Management Report">Case Management Report</div>
                  </Link>
                </li>
              )}
              {canManageReportFeedback && (
                <li className={`menu-item ${location.pathname === "/reports/feedback" ? "active" : ""}`}>
                  <Link to="/reports/feedback" className="menu-link">
                    <i className="menu-icon tf-icons ri-feedback-line"></i>
                    <div data-i18n="Feedback Report">Feedback Report</div>
                  </Link>
                </li>
              )}
              {canManageReportICF && (
                <li className={`menu-item ${location.pathname === "/reports/individual-client-file" ? "active" : ""}`}>
                  <Link to="/reports/individual-client-file" className="menu-link">
                    <i className="menu-icon tf-icons ri-folder-user-line"></i>
                    <div data-i18n="Individual Client File Report">Individual Client File Report</div>
                  </Link>
                </li>
              )}
              {canManageReportExercise && (
                <li className={`menu-item ${location.pathname === "/reports/exercise-monthly" ? "active" : ""}`}>
                  <Link to="/reports/exercise-monthly" className="menu-link">
                    <i className="menu-icon tf-icons ri-run-line"></i>
                    <div data-i18n="Exercise Monthly Report">Exercise Monthly Report</div>
                  </Link>
                </li>
              )}
              {canManageReportADL && (
                <li className={`menu-item ${location.pathname === "/reports/adl-record" ? "active" : ""}`}>
                  <Link to="/reports/adl-record" className="menu-link">
                    <i className="menu-icon tf-icons ri-list-check-2"></i>
                    <div data-i18n="ADL Record Report">ADL Record Report</div>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}

        {/* Logs & Audit Group */}
        {hasLogPermissions && (
          <li
            className={`menu-item ${isLogsOpen ? "open" : ""} ${isLogsActive ? "active" : ""}`}
          >
            <a
              href="#"
              className="menu-link menu-toggle"
              onClick={(e) => {
                e.preventDefault();
                setIsLogsOpen(!isLogsOpen);
              }}
            >
              <i className="menu-icon tf-icons ri-history-line"></i>
              <div data-i18n="Logs & Audit">Logs &amp; Audit</div>
            </a>
            <ul className="menu-sub">
              {canManageFormLogs && (
                <li className={`menu-item ${location.pathname === "/logs/forms" ? "active" : ""}`}>
                  <Link to="/logs/forms" className="menu-link">
                    <i className="menu-icon tf-icons ri-file-list-line"></i>
                    <div data-i18n="Form Logs">Form Logs</div>
                  </Link>
                </li>
              )}
              {canManageAuditLogs && (
                <li className={`menu-item ${location.pathname === "/logs/audit" ? "active" : ""}`}>
                  <Link to="/logs/audit" className="menu-link">
                    <i className="menu-icon tf-icons ri-shield-check-line"></i>
                    <div data-i18n="Audit Logs">Audit Logs</div>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}

        {hasMasterPagesPermissions && (
          <li
            className={`menu-item ${isMasterPagesOpen ? "open" : ""} ${isMasterPagesActive ? "active" : ""}`}
          >
            <a
              href="#"
              className="menu-link menu-toggle"
              onClick={(e) => {
                e.preventDefault();
                setIsMasterPagesOpen(!isMasterPagesOpen);
              }}
            >
              <i className="menu-icon tf-icons ri-sound-module-line"></i>
              <div data-i18n="Master Pages">Master Pages</div>
            </a>
            <ul className="menu-sub">
              {canManageSessionCategories && (
                <li
                  className={`menu-item ${location.pathname.startsWith("/session-categories") ? "active" : ""}`}
                >
                  <Link to="/session-categories" className="menu-link">
                    <i className="menu-icon tf-icons ri-folders-line"></i>
                    <div data-i18n="Session Categories">Session Categories</div>
                  </Link>
                </li>
              )}
              {canManageSessionTypes && (
                <li
                  className={`menu-item ${location.pathname.startsWith("/session-types") ? "active" : ""}`}
                >
                  <Link to="/session-types" className="menu-link">
                    <i className="menu-icon tf-icons ri-calendar-todo-line"></i>
                    <div data-i18n="Session Types">Session Types</div>
                  </Link>
                </li>
              )}
              {canManageSubSessionTypes && (
                <li
                  className={`menu-item ${location.pathname.startsWith("/sub-session-types") ? "active" : ""}`}
                >
                  <Link to="/sub-session-types" className="menu-link">
                    <i className="menu-icon tf-icons ri-calendar-event-line"></i>
                    <div data-i18n="Sub Session Types">Sub Session Types</div>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}

        {/* Administration Group */}
        {hasAdminPermissions && (
          <li
            className={`menu-item ${isAdminOpen ? "open" : ""} ${isAdminActive ? "active" : ""
              }`}
          >
            <a
              href="#"
              className="menu-link menu-toggle"
              onClick={(e) => {
                e.preventDefault();
                setIsAdminOpen(!isAdminOpen);
              }}
            >
              <i className="menu-icon tf-icons ri-admin-line"></i>
              <div data-i18n="Administration">Administration</div>
            </a>
            <ul className="menu-sub">
              {canManageUsers && (
                <li
                  className={`menu-item ${location.pathname === "/users" ? "active" : ""
                    }`}
                >
                  <Link to="/users" className="menu-link">
                    <i className="menu-icon tf-icons ri-user-settings-line"></i>
                    <div data-i18n="Users Management">Users Management</div>
                  </Link>
                </li>
              )}
              {canManageRoles && (
                <li
                  className={`menu-item ${location.pathname === "/roles" ? "active" : ""
                    }`}
                >
                  <Link to="/roles" className="menu-link">
                    <i className="menu-icon tf-icons ri-shield-user-line"></i>
                    <div data-i18n="Roles Management">Roles Management</div>
                  </Link>
                </li>
              )}

            </ul>
          </li>
        )}
        {/* {canManageInjection && (
          <li
            className={`menu-item ${location.pathname.startsWith("/injections") ? "active" : ""
              }`}
          >
            <Link to="/injections" className="menu-link">
              <i className="menu-icon tf-icons ri-syringe-line"></i>
              <div data-i18n="Injection List">Injection Records</div>
            </Link>
          </li>
        )} */}
        {/* {canManageConsent && (
          <li
            className={`menu-item ${location.pathname.startsWith("/consents") ? "active" : ""
              }`}
          >
            <Link to="/consents" className="menu-link">
              <i className="menu-icon tf-icons ri-file-check-line"></i>
              <div data-i18n="Consent List">Consent Form</div>
            </Link>
          </li>
        )}
        {canManageInjection && (
          <li
            className={`menu-item ${location.pathname.startsWith("/injections") ? "active" : ""
              }`}
          >
            <Link to="/injections" className="menu-link">
              <i className="menu-icon tf-icons ri-syringe-line"></i>
              <div data-i18n="Injection List">Injection Records</div>
            </Link>
          </li>
        )}
        {canManageBelonging && (
          <li
            className={`menu-item ${location.pathname.startsWith("/personal-belongings") ? "active" : ""
              }`}
          >
            <Link to="/personal-belongings" className="menu-link">
              <i className="menu-icon tf-icons ri-briefcase-3-line"></i>
              <div data-i18n="Personal Belongings">Personal Belongings</div>
            </Link>
          </li>
        )}
        {canManageAdlsPre && (
          <li
            className={`menu-item ${location.pathname.startsWith("/phase04/adl-pre") ? "active" : ""
              }`}
          >
            <Link to="/phase04/adl-pre" className="menu-link">
              <i className="menu-icon tf-icons ri-wheelchair-line"></i>
              <div data-i18n="ADL Pre Assessment">ADL Pre Assessment</div>
            </Link>
          </li>
        )}
        <li
          className={`menu-item ${location.pathname.startsWith("/phase04/client-history") ? "active" : ""
            }`}
        >
          <Link to="/phase04/client-history" className="menu-link">
            <i className="menu-icon tf-icons ri-contacts-book-2-line"></i>
            <div data-i18n="Client History">Client History</div>
          </Link>
        </li>
        {canManageFamilyPlan && (
          <li
            className={`menu-item ${location.pathname.startsWith("/phase04/tentative-family-plan") ? "active" : ""
              }`}
          >
            <Link to="/phase04/tentative-family-plan" className="menu-link">
              <i className="menu-icon tf-icons ri-parent-line"></i>
              <div data-i18n="Tentative Family Plan">Tentative Family Plan</div>
            </Link>
          </li>
        )}
        {/* <li
          className={`menu-item ${location.pathname.startsWith("/phase05/adl-record-sheet") ? "active" : ""}`}
        >
          <Link to="/phase05/adl-record-sheet" className="menu-link">
            <i className="menu-icon tf-icons ri-wheelchair-line"></i>
            <div data-i18n="ADL Record Sheet">ADL Daily Record Sheet</div>
          </Link>
        </li>
        {canManageFamilyMeeting && (
          <li
            className={`menu-item ${location.pathname.startsWith("/phase05/family-meeting-minutes") ? "active" : ""
              }`}
          >
            <Link to="/phase05/family-meeting-minutes" className="menu-link">
              <i className="menu-icon tf-icons ri-group-line"></i>
              <div data-i18n="Family Meeting Minutes">Family Meeting Minutes</div>
            </Link>
          </li>
        )}
        <li
          className={`menu-item ${location.pathname.startsWith("/phase04/incident-reports") ? "active" : ""}`}
        >
          <Link to="/phase04/incident-reports" className="menu-link">
            <i className="menu-icon tf-icons ri-alert-line"></i>
            <div data-i18n="Incident Reports">Incident Reports</div>
          </Link>
        </li>
        <li
          className={`menu-item ${location.pathname.startsWith("/phase05/individualised-rehab-plans") ? "active" : ""}`}
        >
          <Link to="/phase05/individualised-rehab-plans" className="menu-link">
            <i className="menu-icon tf-icons ri-file-list-line"></i>
            <div data-i18n="Individualised Rehab Plans">Individualised Rehab</div>
          </Link>
        </li>
        <li
          className={`menu-item ${location.pathname.startsWith("/phase05/medication-sheets") ? "active" : ""
            }`}
        >
          <Link to="/phase05/medication-sheets" className="menu-link">
            <i className="menu-icon tf-icons ri-capsule-line"></i>
            <div data-i18n="Medication Sheets">Medication Sheet</div>
          </Link>
        </li>
        {canManageSanaNotes && (
          <li
            className={`menu-item ${location.pathname.startsWith("/phase05/sana-notes") ? "active" : ""
              }`}
          >
            <Link to="/phase05/sana-notes" className="menu-link">
              <i className="menu-icon tf-icons ri-file-list-3-line"></i>
              <div data-i18n="Sana Notes">Sana Notes</div>
            </Link>
          </li>
        )}
        {canManageNursingRecord && (
          <li
            className={`menu-item ${location.pathname.startsWith("/phase05/nursing-records") ? "active" : ""
              }`}
          >
            <Link to="/phase05/nursing-records" className="menu-link">
              <i className="menu-icon tf-icons ri-stethoscope-line"></i>
              <div data-i18n="Nursing Records">Nursing Records</div>
            </Link>
          </li>
        )}
        {canManageProgressNote && (
          <li
            className={`menu-item ${location.pathname.startsWith("/phase05/progress-notes") ? "active" : ""}`}
          >
            <Link to="/phase05/progress-notes" className="menu-link">
              <i className="menu-icon tf-icons ri-clipboard-line"></i>
              <div data-i18n="Progress Notes">Progress Notes</div>
            </Link>
          </li>
        )}*
        <li
          className={`menu-item ${location.pathname.startsWith("/phase05/pros-assessments") ? "active" : ""
            }`}
        >
          <Link to="/phase05/pros-assessments" className="menu-link">
            <i className="menu-icon tf-icons ri-psychotherapy-line"></i>
            <div data-i18n="PROS Assessments">PROS Assessments</div>
          </Link>
        </li>
        {canManageDepotInjection && (
          <li
            className={`menu-item ${location.pathname.startsWith("/phase05/depot-injections") ? "active" : ""
              }`}
          >
            <Link to="/phase05/depot-injections" className="menu-link">
              <i className="menu-icon tf-icons ri-syringe-line"></i>
              <div data-i18n="Depot Injections">Depot & Injection Charts</div>
            </Link>
          </li>
        )}
        {canManageVitalsMonitor && (
          <li
            className={`menu-item ${location.pathname.startsWith("/phase05/vitals-monitor") ? "active" : ""}`}
          >
            <Link to="/phase05/vitals-monitor" className="menu-link">
              <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
              <div data-i18n="Vitals Monitoring Chart">Vitals Monitoring Chart</div>
            </Link>
          </li>
        )}
        <li
          className={`menu-item ${location.pathname.startsWith("/phase06/adl-mid") ? "active" : ""}`}
        >
          <Link to="/phase06/adl-mid" className="menu-link">
            <i className="menu-icon tf-icons ri-wheelchair-line"></i>
            <div data-i18n="ADL Mid Assessment">ADL Mid Assessment</div>
          </Link>
        </li>*/}
        {/* <li
          className={`menu-item ${location.pathname.startsWith("/phase07/discharge-advice") ? "active" : ""}`}
        >
          <Link to="/phase07/discharge-advice" className="menu-link">
            <i className="ri-hospital-line me-2"></i>
            <div data-i18n="ADL Mid Assessment">Discharge Advice</div>
          </Link>
        </li> */}
        {/*
        <li
          className={`menu-item ${location.pathname.startsWith("/phase07/adl-post") ? "active" : ""}`}
        >
          <Link to="/phase07/adl-post" className="menu-link">
            <i className="menu-icon tf-icons ri-wheelchair-line"></i>
            <div data-i18n="ADL Mid Assessment">ADL Post Assessment</div>
          </Link>
        </li>
        <li
          className={`menu-item ${location.pathname.startsWith("/phase07/client-satisfaction-drp")
            ? "active"
            : ""
            }`}
        >
          <Link to="/phase07/client-satisfaction-drp" className="menu-link">
            <i className="menu-icon tf-icons ri-user-smile-line"></i>
            <div data-i18n="Client Satisfaction DRP">Client Satisfaction DRP</div>
          </Link>
        </li>
        <li
          className={`menu-item ${location.pathname.startsWith("/phase07/client-satisfaction-residential")
            ? "active"
            : ""
            }`}
        >
          <Link to="/phase07/client-satisfaction-residential" className="menu-link">
            <i className="menu-icon tf-icons ri-user-smile-line"></i>
            <div data-i18n="Client Satisfaction Residential">Client Satisfaction Residential</div>
          </Link>
        </li>
        <li
          className={`menu-item ${location.pathname.startsWith("/phase07/family-satisfaction-drp")
            ? "active"
            : ""
            }`}
        >
          <Link to="/phase07/family-satisfaction-drp" className="menu-link">
            <i className="menu-icon tf-icons ri-user-smile-line"></i>
            <div data-i18n="Family Satisfaction DRP">Family Satisfaction DRP</div>
          </Link>
        </li>
        <li
          className={`menu-item ${location.pathname.startsWith("/phase07/family-satisfaction-residential")
            ? "active"
            : ""
            }`}
        >
          <Link to="/phase07/family-satisfaction-residential" className="menu-link">
            <i className="menu-icon tf-icons ri-user-smile-line"></i>
            <div data-i18n="Family Satisfaction Residential">Family Satisfaction Residential</div>
          </Link>
        </li> */}
        {/* Discharge Interview Clients */}
        {/* <li
          className={`menu-item ${location.pathname.startsWith("/phase-07/discharge-interview-client")
            ? "active"
            : ""
            }`}
        >
          <Link to="/phase-07/discharge-interview-client" className="menu-link">
            <i className="menu-icon tf-icons ri-user-2-line"></i>
            <div data-i18n="Discharge Interview Clients">Discharge Interview Clients</div>
          </Link>
        </li> */}

        {/* Discharge Interview Families */}
        {/* <li
          className={`menu-item ${location.pathname.startsWith("/phase-07/discharge-interview-family")
            ? "active"
            : ""
            }`}
        >
          <Link to="/phase-07/discharge-interview-family" className="menu-link">
            <i className="menu-icon tf-icons ri-user-star-line"></i>
            <div data-i18n="Discharge Interview Families">Discharge Interview Families</div>
          </Link>
        </li> */}
        {/* Discharge Summary (Phase-07) */}
        {/* <li
          className={`menu-item ${location.pathname.startsWith("/phase07/discharge-summary") ? "active" : ""
            }`}
        >
          <Link to="/phase07/discharge-summary" className="menu-link">
            <i className="menu-icon tf-icons ri-file-list-line"></i>
            <div data-i18n="Discharge Summary">Discharge Summary</div>
          </Link>
        </li> */}

        {/* Phase - 04 Group */}
        {/* <li
          className={`menu-item ${isPhase04Open ? "open" : ""
            } ${location.pathname.startsWith("/phase04") ? "active" : ""}`}
        >
          <a
            href="#"
            className="menu-link menu-toggle"
            onClick={(e) => {
              e.preventDefault();
              setIsPhase04Open(!isPhase04Open);
            }}
          >
            <i className="menu-icon tf-icons ri-list-settings-line"></i>
            <div data-i18n="Phase 04">Phase - 04</div>
          </a>

          <ul className="menu-sub">
            <li
              className={`menu-item ${location.pathname === "/phase04/PersonalBelongings" ? "active" : ""
                }`}
            >
              <Link to="/PersonalBelongings" className="menu-link">
                <i className="menu-icon tf-icons ri-briefcase-3-line"></i>
                <div data-i18n="Personal Belongings">Personal Belongings</div>
              </Link>
            </li>

            <li
              className={`menu-item ${location.pathname === "/phase04/family-plan" ? "active" : ""
                }`}
            >
              <Link to="/phase04/family-plan" className="menu-link">
                <i className="menu-icon tf-icons ri-group-line"></i>
                <div data-i18n="Tentative Family Plan">Tentative Family Plan</div>
              </Link>
            </li>

            <li
              className={`menu-item ${location.pathname === "/phase04/gp-examination" ? "active" : ""
                }`}
            >
              <Link to="/phase04/gp-examination" className="menu-link">
                <i className="menu-icon tf-icons ri-stethoscope-line"></i>
                <div data-i18n="GP Examination">GP Examination</div>
              </Link>
            </li>
          </ul>
        </li> */}
        {/* <li
          className={`menu-item ${location.pathname.startsWith("/phase04/anxiety") ? "active" : ""
            }`}
        >
          <Link to="/phase04/anxiety" className="menu-link">
            <i className="menu-icon tf-icons ri-file-list-line"></i>
            <div data-i18n="Anxiety">Anxiety</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/phase04/bdi") ? "active" : ""}`}>
          <Link to="/phase04/bdi" className="menu-link">
            <i className="menu-icon tf-icons ri-emotion-sad-line"></i>
            <div data-i18n="Depression">Depression (BDI-II)</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/phase04/suicide-intent") ? "active" : ""}`}>
          <Link to="/phase04/suicide-intent" className="menu-link">
            <i className="menu-icon tf-icons ri-alarm-warning-line"></i>
            <div data-i18n="Suicide Intent Scale">Suicide Intent Scale</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/phase04/bprs") ? "active" : ""}`}>
          <Link to="/phase04/bprs" className="menu-link">
            <i className="menu-icon tf-icons ri-brain-line"></i>
            <div data-i18n="BPRS">BPRS</div>
          </Link>
        </li>*/}
        {/* <li className={`menu-item ${location.pathname.startsWith("/phase04/phq") ? "active" : ""}`}>
          <Link to="/phase04/phq" className="menu-link">
            <i className="menu-icon tf-icons ri-emotion-unhappy-line"></i>
            <div data-i18n="PHQ-9">PHQ-9</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/phase04/obsessive-compulsive-test") ? "active" : ""}`}>
          <Link to="/phase04/obsessive-compulsive-test" className="menu-link">
            <i className="menu-icon tf-icons ri-loop-left-line"></i>
            <div data-i18n="Obsessive Compulsive Test">Obsessive Compulsive Test (YBOCS)</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/phase04/qol") ? "active" : ""}`}>
          <Link to="/phase04/qol" className="menu-link">
            <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
            <div data-i18n="QOL">QOLS</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/phase04/ymrs") ? "active" : ""}`}>
          <Link to="/phase04/ymrs" className="menu-link">
            <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
            <div data-i18n="YMRS">YMRS</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/phase03/psychiatrist-order") ? "active" : ""}`}>
          <Link to="/phase03/psychiatrist-order" className="menu-link">
            <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
            <div data-i18n="Psychiatrist Order">Psychiatrist Order</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/phase03/complaint-form") ? "active" : ""}`}>
          <Link to="/phase03/complaint-form" className="menu-link">
            <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
            <div data-i18n="Complaint Form">Complaint Form</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/home-visit/health-safety") ? "active" : ""}`}>
          <Link to="/home-visit/health-safety" className="menu-link">
            <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
            <div data-i18n="Health Safety">Health & Safety</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/home-visit/treatment-plan") ? "active" : ""}`}>
          <Link to="/home-visit/treatment-plan" className="menu-link">
            <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
            <div data-i18n="Treatment Plan">Treatment Plan</div>
          </Link>
        </li> */}
        {/* <li className={`menu-item ${location.pathname.startsWith("/residential/phase-01/integrated-initial-assessment") ? "active" : ""}`}>
          <Link to="/residential/phase-01/integrated-initial-assessment" className="menu-link">
            <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
            <div data-i18n="Integrated Initial Assessment">Integrated Initial Assessment</div>
          </Link>
        </li> */}
        {/* <li
          className={`menu-item ${location.pathname.startsWith("/phase04/incident-reports") ? "active" : ""}`}
        >
          <Link to="/phase04/incident-reports" className="menu-link">
            <i className="menu-icon tf-icons ri-alert-line"></i>
            <div data-i18n="Incident Reports">Incident Reports</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/residential/optional/lama-form") ? "active" : ""}`}>
          <Link to="/residential/optional/lama-form" className="menu-link">
            <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
            <div data-i18n="LAMA Form">LAMA Form</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/residential/optional/referral-form") ? "active" : ""}`}>
          <Link to="/residential/optional/referral-form" className="menu-link">
            <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
            <div data-i18n="Referral Form">Referral Form</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/residential/optional/relocation-form") ? "active" : ""}`}>
          <Link to="/residential/optional/relocation-form" className="menu-link">
            <i className="menu-icon tf-icons ri-heart-pulse-line"></i>
            <div data-i18n="Relocation Form">Relocation Form</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/residential/optional/safety-plan") ? "active" : ""}`}>
          <Link to="/residential/optional/safety-plan" className="menu-link">
            <i className="menu-icon tf-icons ri-shield-check-line"></i>
            <div data-i18n="Safety Plan">Safety Plan</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/residential/optional/violence-risk") ? "active" : ""}`}>
          <Link to="/residential/optional/violence-risk" className="menu-link">
            <i className="menu-icon tf-icons ri-alarm-warning-line"></i>
            <div data-i18n="Violence Risk Assessment">Violence Risk Assessment (V-RISK-10)</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/residential/optional/suicide-detail") ? "active" : ""}`}>
          <Link to="/residential/optional/suicide-detail" className="menu-link">
            <i className="menu-icon tf-icons ri-alarm-warning-line"></i>
            <div data-i18n="Suicide Risk Screener">Suicide Risk Screener</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/exercise-schedule") ? "active" : ""}`}>
          <Link to="/exercise-schedule" className="menu-link">
            <i className="menu-icon tf-icons ri-run-line"></i>
            <div data-i18n="Weekly Exercise Schedule">Weekly Exercise Schedule</div>
          </Link>
        </li>
        <li className={`menu-item ${location.pathname.startsWith("/residential/optional/safety-plan-urdu") ? "active" : ""}`}>
          <Link to="/residential/optional/safety-plan-urdu" className="menu-link">
            <i className="menu-icon tf-icons ri-shield-check-line"></i>
            <div data-i18n="Safety Plan (Urdu)">Safety Plan (Urdu)</div>
          </Link>
        </li> */}
      </ul>
    </aside>
  );
};

export default Sidebar;
